import { useState, useRef, useEffect } from 'react';
import { ChatRequest } from '../types/api';
import LexAIService from '../services/api';

interface ChatMessage {
  id: string;
  message: string;
  sender: 'user' | 'lex';
  timestamp: Date;
  agentExecutions?: any[];
}

interface UseChatMessagesProps {
  userProfile?: any;
  conversationId: string;
}

interface UseChatMessagesReturn {
  messages: ChatMessage[];
  inputMessage: string;
  setInputMessage: (message: string) => void;
  isLoading: boolean;
  messagesEndRef: React.RefObject<HTMLDivElement | null>;
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
  sendMessage: () => Promise<void>;
  handleKeyPress: (e: React.KeyboardEvent) => void;
  handleTextareaChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  clearMessages: () => void;
}

export const useChatMessages = ({
  userProfile,
  conversationId
}: UseChatMessagesProps): UseChatMessagesReturn => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage: ChatMessage = {
      id: `user_${Date.now()}`,
      message: inputMessage,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    const messageToSend = inputMessage;
    setInputMessage('');
    setIsLoading(true);

    try {
      const request: ChatRequest = {
        message: messageToSend,
        userId: userProfile?.id || 'dashboard_user',
        conversationId: conversationId,
        userName: userProfile?.displayName || 'Dashboard User',
        userEmail: userProfile?.mail || userProfile?.userPrincipalName || 'user@prosperfinance.com.au'
      };

      const response = await LexAIService.sendChatMessage(request);

      if (response) {
        const lexMessage: ChatMessage = {
          id: `lex_${Date.now()}`,
          message: response.message,
          sender: 'lex',
          timestamp: new Date(),
          agentExecutions: response.agentExecutions
        };
        setMessages(prev => [...prev, lexMessage]);
      } else {
        const errorMessage: ChatMessage = {
          id: `error_${Date.now()}`,
          message: "Sorry, I'm having trouble connecting to the backend. The API might be down. Please try again in a moment.",
          sender: 'lex',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, errorMessage]);
      }
    } catch (error) {
      const isNetworkError = error instanceof Error && error.message.includes('Network Error');
      const errorMessage: ChatMessage = {
        id: `error_${Date.now()}`,
        message: isNetworkError
          ? "🔌 Connection error: Can't reach the LexAI backend server. Please make sure it's running on port 8080."
          : "⚠️ Something went wrong while processing your message. Please try again.",
        sender: 'lex',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputMessage(e.target.value);

    // Auto-resize textarea
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  };

  const clearMessages = () => {
    // Reset to empty messages
    setMessages([]);
    setInputMessage('');
  };

  return {
    messages,
    inputMessage,
    setInputMessage,
    isLoading,
    messagesEndRef,
    textareaRef,
    sendMessage,
    handleKeyPress,
    handleTextareaChange,
    clearMessages
  };
};
